import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Application configuration
const APPLICATIONS = {
  PART1: {
    id: "PART1",
    name: "MISSING: The Search for Terry Hughes",
    shortName: "MISSING Part I",
    totalCheckpoints: 90
  },
  PART2: {
    id: "PART2",
    name: "MISSING: The Sequel",
    shortName: "MISSING Part II",
    totalCheckpoints: 100
  }
};

function BuyerDashboard() {
  const navigate = useNavigate();
  const [buyerData, setBuyerData] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [userProgress, setUserProgress] = useState({});
  const [loadingProgress, setLoadingProgress] = useState({});
  const [selectedApplication, setSelectedApplication] = useState("PART1");
  const [selfAssignedApps, setSelfAssignedApps] = useState([]);
  const [buttonStates, setButtonStates] = useState({});
  
  // CSV Upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [showBulkResults, setShowBulkResults] = useState(false);

  // ─── Archive feature state ───
  const [viewMode, setViewMode] = useState("active"); // "active" | "archive"
  const [selectedForArchive, setSelectedForArchive] = useState(new Set());
  const [selectedForRestore, setSelectedForRestore] = useState(new Set());
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // ─── Search & Sort state ───
  const [searchQuery, setSearchQuery] = useState("");
  const [sortMode, setSortMode] = useState("default");

  // ─── Tag system state ───
  const [tagFilter, setTagFilter] = useState("__ALL__"); // "__ALL__" | "__UNTAGGED__" | "<tagName>"
  const [showTagManager, setShowTagManager] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [renamingTag, setRenamingTag] = useState(null); // { oldName, newName }
  const [tagActionLoading, setTagActionLoading] = useState(false);
  const [showTagAssign, setShowTagAssign] = useState(false);
  const [assignTagValue, setAssignTagValue] = useState("");

  // ─── Bulk action state ───
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }
    
    fetchBuyerDetails();
    
    const timer = setTimeout(() => {
      setError("");
      setBulkResults(null);
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [navigate]);

  // Fetch progress for users when buyer data or selected application changes
  useEffect(() => {
    if (buyerData && buyerData.Users && buyerData.Users.length > 0) {
      buyerData.Users.forEach(user => {
        const userApps = user.applications || ["PART1"];
        if (userApps.includes(selectedApplication)) {
          fetchUserProgress(user.userEmail, selectedApplication);
        }
      });
    }
  }, [buyerData, selectedApplication]);

  async function fetchBuyerDetails() {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/details", {
        headers: { 
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching details: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.ApplicationLicenses) {
        data.ApplicationLicenses = { 
          "PART1": data.TotalLicenses || 0 
        };
      }
      
      if (data.Users && data.Users.length > 0) {
        data.Users = data.Users.map(user => {
          if (!user.applications) {
            return { ...user, applications: ["PART1"] };
          }
          return user;
        });
      }
      
      setBuyerData(data);
    } catch (err) {
      console.error("Error fetching buyer details:", err);
      setError("Failed to load license data. Please try again later.");
    } finally {
      setLoading(false);
    }
  }

  // Determine self-assigned applications
  useEffect(() => {
    if (buyerData && buyerData.Users) {
      const selfUser = buyerData.Users.find(user => user.userEmail === buyerData.BuyerEmail);
      if (selfUser) {
        setSelfAssignedApps(selfUser.applications || ["PART1"]);
      } else {
        setSelfAssignedApps([]);
      }
    }
  }, [buyerData]);

  // Fetch user progress for a specific application
  async function fetchUserProgress(userEmail, application) {
    const progressKey = `${userEmail}-${application}`;
    
    if (loadingProgress[progressKey]) return;

    setLoadingProgress(prev => ({ ...prev, [progressKey]: true }));
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/buyer/user-progress/${encodeURIComponent(userEmail)}?application=${application}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          setUserProgress(prev => ({ 
            ...prev, 
            [progressKey]: { loaded: true, exists: false, data: null, application }
          }));
          return;
        }
        throw new Error(`Error fetching user progress: ${response.status}`);
      }
      
      const responseData = await response.json();
      let parsedData = null;
      
      if (responseData.progress && responseData.progress.parsedData) {
        const serverParsedData = responseData.progress.parsedData;
        parsedData = {
          playTime: serverParsedData.playTime || 0,
          checkPoint: serverParsedData.checkPoint || 0,
          aarCheckpoint: serverParsedData.aarCheckpoint || 0,
          gameComplete: serverParsedData.gameComplete || serverParsedData.gameCompleted || false,
          dateCompleted: serverParsedData.dateCompleted,
          profileName: serverParsedData.profileName,
          position: serverParsedData.position,
          lastUpdated: responseData.progress.lastUpdated,
          application
        };
      } else if (responseData.progress && responseData.progress.saveData) {
        try {
          const saveDataObj = JSON.parse(responseData.progress.saveData);
          if (Array.isArray(saveDataObj) && saveDataObj.length > 0) {
            for (let i = 0; i < saveDataObj.length; i++) {
              const entry = saveDataObj[i];
              if (entry && entry !== null) {
                const hasPlayTime = entry.playTime !== undefined && entry.playTime > 0;
                const hasCheckpoint = entry.checkPoint !== undefined && entry.checkPoint > 0;
                const hasProfileName = entry.profileName !== undefined && entry.profileName.trim() !== "";
                const hasGameComplete = entry.gameComplete !== undefined || entry.gameCompleted !== undefined;
                
                if (hasPlayTime || hasCheckpoint || hasProfileName || hasGameComplete) {
                  parsedData = {
                    playTime: entry.playTime || 0,
                    checkPoint: entry.checkPoint || 0,
                    aarCheckpoint: entry.aarCheckpoint || 0,
                    gameComplete: entry.gameComplete || entry.gameCompleted || false,
                    dateCompleted: entry.dateCompleted,
                    profileName: entry.profileName,
                    position: i,
                    lastUpdated: responseData.progress.lastUpdated,
                    application
                  };
                  break;
                }
              }
            }
          }
        } catch (parseError) {
          console.error(`Error parsing save data for user ${userEmail} and application ${application}:`, parseError);
        }
      }
      
      setUserProgress(prev => ({ 
        ...prev, 
        [progressKey]: { loaded: true, exists: !!parsedData, data: parsedData, application }
      }));
    } catch (err) {
      console.error(`Error fetching progress for user ${userEmail} and application ${application}:`, err);
      setUserProgress(prev => ({ 
        ...prev, 
        [progressKey]: { loaded: true, exists: false, error: err.message, application }
      }));
    } finally {
      setLoadingProgress(prev => ({ ...prev, [progressKey]: false }));
    }
  }

  // =====================================================
  //  LICENSE ASSIGNMENT
  // =====================================================

  async function assignLicense() {
    if (!newUserEmail) {
      setError("Please enter a user email address.");
      return;
    }
    
    setLoading(true);
    setError("");
    setSuccessMessage("");
    
    try {
      const token = localStorage.getItem("token");
      const applicationLicenses = buyerData.ApplicationLicenses || {};
      const totalLicensesForApp = applicationLicenses[selectedApplication] || 0;
      const usersWithApp = buyerData.Users?.filter(user => {
        const userApps = user.applications || ["PART1"];
        return userApps.includes(selectedApplication);
      }).length || 0;
      
      if (usersWithApp >= totalLicensesForApp) {
        throw new Error(`No licenses available for ${APPLICATIONS[selectedApplication].shortName}`);
      }
      
      const response = await fetch("/buyer/assign-user", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userEmail: newUserEmail, application: selectedApplication })
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not assign license");
      }

      setSuccessMessage(`License for ${APPLICATIONS[selectedApplication].shortName} assigned to ${newUserEmail} successfully.`);
      setNewUserEmail("");
      await fetchBuyerDetails();
    } catch (err) {
      console.error("Error assigning license:", err);
      setError(err.message || "Failed to assign license. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // CSV Upload Handler
  async function handleCsvUpload() {
    if (!csvFile) {
      setError("Please select a CSV file to upload.");
      return;
    }

    setCsvUploading(true);
    setError("");
    setSuccessMessage("");
    setBulkResults(null);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('application', selectedApplication);

      const response = await fetch("/buyer/bulk-assign-users", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not process CSV upload");
      }

      const responseData = await response.json();
      setBulkResults(responseData.results);
      setShowBulkResults(true);
      
      if (responseData.results.successful > 0) {
        setSuccessMessage(
          `Successfully assigned ${responseData.results.successful} users to ${APPLICATIONS[selectedApplication].shortName}. ` +
          `${responseData.results.invitationsSent} invitation emails sent.`
        );
      }

      setCsvFile(null);
      await fetchBuyerDetails();
    } catch (err) {
      console.error("Error uploading CSV:", err);
      setError(err.message || "Failed to process CSV upload. Please try again.");
    } finally {
      setCsvUploading(false);
    }
  }

  async function revokeLicense(emailToRevoke) {
    if (!window.confirm(`Are you sure you want to revoke the ${APPLICATIONS[selectedApplication].shortName} license for ${emailToRevoke}?`)) {
      return;
    }
    
    setLoading(true);
    setError("");
    setSuccessMessage("");
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/revoke-user", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userEmail: emailToRevoke, application: selectedApplication })
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not revoke license");
      }

      setSuccessMessage(`${APPLICATIONS[selectedApplication].shortName} license for ${emailToRevoke} revoked successfully.`);
      await fetchBuyerDetails();
    } catch (err) {
      console.error("Error revoking license:", err);
      setError(err.message || "Failed to revoke license. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function inviteUser(userEmail) {
    setLoading(true);
    setError("");
    setSuccessMessage("");
    
    try {
      const token = localStorage.getItem("token");
      
      const storeResponse = await fetch("/buyer/store-invitation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: userEmail,
          application: selectedApplication,
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        })
      });
      
      if (!storeResponse.ok) {
        const errorData = await storeResponse.json();
        throw new Error(errorData.error || "Failed to store invitation");
      }
      
      const storeData = await storeResponse.json();
      
      await fetch("/buyer/send-invitation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: userEmail,
          inviteToken: storeData.token,
          application: selectedApplication,
          emailSubject: `Welcome to ${APPLICATIONS[selectedApplication].shortName} - User Account Invitation`,
          appName: APPLICATIONS[selectedApplication].name
        })
      });
      
      const actionWord = storeData.isResend ? "resent" : "sent";
      setSuccessMessage(`${APPLICATIONS[selectedApplication].shortName} invitation ${actionWord} to ${userEmail} successfully.`);
    } catch (err) {
      console.error("Error sending invitation:", err);
      setError(`Failed to send invitation: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  // =====================================================
  //  ARCHIVE / RESTORE ACTIONS
  // =====================================================

  async function archiveSelectedUsers() {
    if (selectedForArchive.size === 0) return;
    
    if (!window.confirm(`Archive ${selectedForArchive.size} user(s)? They will be moved to the archive view.`)) {
      return;
    }

    setArchiving(true);
    setError("");
    setSuccessMessage("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/archive-users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userEmails: Array.from(selectedForArchive) })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not archive users");
      }

      const data = await response.json();
      setSuccessMessage(data.message);
      setSelectedForArchive(new Set());
      await fetchBuyerDetails();
    } catch (err) {
      console.error("Error archiving users:", err);
      setError(err.message || "Failed to archive users.");
    } finally {
      setArchiving(false);
    }
  }

  async function restoreSelectedUsers() {
    if (selectedForRestore.size === 0) return;

    if (!window.confirm(`Restore ${selectedForRestore.size} user(s) from the archive?`)) {
      return;
    }

    setRestoring(true);
    setError("");
    setSuccessMessage("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/restore-users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userEmails: Array.from(selectedForRestore) })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not restore users");
      }

      const data = await response.json();
      setSuccessMessage(data.message);
      setSelectedForRestore(new Set());
      await fetchBuyerDetails();
    } catch (err) {
      console.error("Error restoring users:", err);
      setError(err.message || "Failed to restore users.");
    } finally {
      setRestoring(false);
    }
  }

  // =====================================================
  //  TAG MANAGEMENT
  // =====================================================

  const buyerTags = buyerData?.tags || [];

  async function createTag() {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    if (buyerTags.includes(trimmed)) {
      setError(`Tag "${trimmed}" already exists.`);
      return;
    }

    setTagActionLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/manage-tags", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "create", tagName: trimmed })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not create tag");
      }
      setNewTagName("");
      await fetchBuyerDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setTagActionLoading(false);
    }
  }

  async function renameTag(oldName, newName) {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) { setRenamingTag(null); return; }
    if (buyerTags.includes(trimmed)) {
      setError(`Tag "${trimmed}" already exists.`);
      return;
    }

    setTagActionLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/manage-tags", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "rename", oldTagName: oldName, newTagName: trimmed })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not rename tag");
      }
      setRenamingTag(null);
      if (tagFilter === oldName) setTagFilter(trimmed);
      await fetchBuyerDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setTagActionLoading(false);
    }
  }

  async function deleteTag(tagName) {
    if (!window.confirm(`Delete tag "${tagName}"? Users with this tag will become untagged.`)) return;

    setTagActionLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/manage-tags", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "delete", tagName })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not delete tag");
      }
      if (tagFilter === tagName) setTagFilter("__ALL__");
      await fetchBuyerDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setTagActionLoading(false);
    }
  }

  async function assignTagToSelected() {
    const emails = Array.from(viewMode === "active" ? selectedForArchive : selectedForRestore);
    if (emails.length === 0) return;

    const tagValue = assignTagValue === "__REMOVE__" ? "" : assignTagValue;
    const label = tagValue ? `"${tagValue}"` : "no tag (remove)";
    if (!window.confirm(`Assign ${label} to ${emails.length} selected user(s)?`)) return;

    setTagActionLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/assign-tag", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userEmails: emails, tag: tagValue })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not assign tag");
      }
      const data = await response.json();
      setSuccessMessage(data.message);
      setShowTagAssign(false);
      setAssignTagValue("");
      if (viewMode === "active") setSelectedForArchive(new Set());
      else setSelectedForRestore(new Set());
      await fetchBuyerDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setTagActionLoading(false);
    }
  }

  // =====================================================
  //  BULK REVOKE & RESEND INVITATION
  // =====================================================

  async function bulkRevokeSelected() {
    const emails = Array.from(selectedForArchive);
    if (emails.length === 0) return;
    if (!window.confirm(
      `Revoke ${APPLICATIONS[selectedApplication].shortName} license for ${emails.length} selected user(s)?\n\n` +
      `Note: Only un-activated licenses can be revoked. Already-activated users will be skipped.`
    )) return;

    setBulkActionLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/bulk-revoke", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userEmails: emails, application: selectedApplication })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not revoke licenses");
      }
      const data = await response.json();
      setSuccessMessage(data.message);
      setSelectedForArchive(new Set());
      await fetchBuyerDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkActionLoading(false);
    }
  }

  async function bulkResendInvitations() {
    const emails = Array.from(selectedForArchive);
    if (emails.length === 0) return;
    if (!window.confirm(
      `Resend invitation for ${APPLICATIONS[selectedApplication].shortName} to ${emails.length} selected user(s)?\n\n` +
      `Note: Only un-activated users without a Cognito account will receive invitations. Others will be skipped.`
    )) return;

    setBulkActionLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/buyer/bulk-resend-invitation", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userEmails: emails, application: selectedApplication })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Could not resend invitations");
      }
      const data = await response.json();
      setSuccessMessage(data.message);
      setSelectedForArchive(new Set());
      await fetchBuyerDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkActionLoading(false);
    }
  }

  // =====================================================
  //  SELECTION HELPERS
  // =====================================================

  const toggleArchiveSelection = (email) => {
    setSelectedForArchive(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleRestoreSelection = (email) => {
    setSelectedForRestore(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleSelectAllArchive = (users) => {
    const emails = users.map(u => u.userEmail);
    const allSelected = emails.every(e => selectedForArchive.has(e));
    if (allSelected) {
      setSelectedForArchive(new Set());
    } else {
      setSelectedForArchive(new Set(emails));
    }
  };

  const toggleSelectAllRestore = (users) => {
    const emails = users.map(u => u.userEmail);
    const allSelected = emails.every(e => selectedForRestore.has(e));
    if (allSelected) {
      setSelectedForRestore(new Set());
    } else {
      setSelectedForRestore(new Set(emails));
    }
  };

  // =====================================================
  //  UTILITY FUNCTIONS
  // =====================================================

  const formatPlayTime = (seconds) => {
    if (!seconds && seconds !== 0) return "N/A";
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min`;
  };
  
  const formatDate = (dateString) => {
    if (!dateString || dateString === "0001-01-01T00:00:00") return "N/A";
    return new Date(dateString).toLocaleDateString();
  };
  
  const calculateCheckpointPercentage = (checkpoint, application) => {
    if (checkpoint === null || checkpoint === undefined) return "0%";
    const totalCheckpoints = APPLICATIONS[application].totalCheckpoints;
    const percentage = Math.round((checkpoint / totalCheckpoints) * 100);
    return `${percentage}%`;
  };

  const getCheckpointNumeric = (userEmail, application) => {
    const progressKey = `${userEmail}-${application}`;
    const progress = userProgress[progressKey];
    if (!progress || !progress.exists || !progress.data) return -1; // no data sorts last
    const totalCheckpoints = APPLICATIONS[application].totalCheckpoints;
    return Math.round((progress.data.checkPoint / totalCheckpoints) * 100);
  };
  
  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const getUserProgressData = (userEmail, application) => {
    const progressKey = `${userEmail}-${application}`;
    return userProgress[progressKey] || { loaded: false, exists: false, data: null, application };
  };

  const userHasApplicationAccess = (user, application) => {
    const userApps = user.applications || ["PART1"];
    return userApps.includes(application);
  };

  const userHasActivatedApplication = (user, application) => {
    const activatedApps = user.activatedApps || [];
    return activatedApps.includes(application);
  };

  const getActivatedUsersCount = (application) => {
    return buyerData?.Users?.filter(user => {
      return userHasApplicationAccess(user, application) && userHasActivatedApplication(user, application);
    }).length || 0;
  };

  const hasLicenseForApplication = (application) => {
    const applicationLicenses = buyerData?.ApplicationLicenses || {};
    return application in applicationLicenses;
  };

  // =====================================================
  //  USER FILTERING, SEARCH, SORT & TAG FILTER
  // =====================================================

  const getUsersWithSelectedApplication = useCallback(() => {
    const users = buyerData?.Users?.filter(user => userHasApplicationAccess(user, selectedApplication)) || [];
    return users;
  }, [buyerData, selectedApplication]);

  // Split users into active and archived sets, apply tag filter, search, sort
  const getFilteredSortedUsers = useCallback(() => {
    const allUsers = getUsersWithSelectedApplication();
    
    // Split by archive status
    const activeUsers = allUsers.filter(u => !u.archived);
    const archivedUsers = allUsers.filter(u => u.archived);

    // Pick which set we're displaying
    let displayUsers = viewMode === "active" ? activeUsers : archivedUsers;

    // Tag filter (applies in both active and archive views)
    if (tagFilter === "__UNTAGGED__") {
      displayUsers = displayUsers.filter(u => !u.tag);
    } else if (tagFilter !== "__ALL__") {
      displayUsers = displayUsers.filter(u => u.tag === tagFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      displayUsers = displayUsers.filter(u => u.userEmail.toLowerCase().includes(q));
    }

    // Sort — self-assigned buyer always first in active view
    displayUsers = [...displayUsers].sort((a, b) => {
      // Self-assigned buyer pinned to top in active view
      if (viewMode === "active") {
        if (a.userEmail === buyerData?.BuyerEmail) return -1;
        if (b.userEmail === buyerData?.BuyerEmail) return 1;
      }

      switch (sortMode) {
        case "alpha-asc":
          return a.userEmail.localeCompare(b.userEmail);
        case "alpha-desc":
          return b.userEmail.localeCompare(a.userEmail);
        case "date-asc": {
          const dateA = viewMode === "archive" ? (a.archivedAt || "") : (a.activatedAt || a.assignedAt || "");
          const dateB = viewMode === "archive" ? (b.archivedAt || "") : (b.activatedAt || b.assignedAt || "");
          return dateA.localeCompare(dateB);
        }
        case "date-desc": {
          const dateA = viewMode === "archive" ? (a.archivedAt || "") : (a.activatedAt || a.assignedAt || "");
          const dateB = viewMode === "archive" ? (b.archivedAt || "") : (b.activatedAt || b.assignedAt || "");
          return dateB.localeCompare(dateA);
        }
        case "progress-desc": {
          const pA = getCheckpointNumeric(a.userEmail, selectedApplication);
          const pB = getCheckpointNumeric(b.userEmail, selectedApplication);
          return pB - pA; // high to low
        }
        case "progress-asc": {
          const pA = getCheckpointNumeric(a.userEmail, selectedApplication);
          const pB = getCheckpointNumeric(b.userEmail, selectedApplication);
          return pA - pB; // low to high
        }
        default:
          return 0;
      }
    });

    return { displayUsers, activeCount: activeUsers.length, archivedCount: archivedUsers.length };
  }, [getUsersWithSelectedApplication, viewMode, tagFilter, searchQuery, sortMode, buyerData, userProgress, selectedApplication]);

  // Clear selections when switching views or apps
  useEffect(() => {
    setSelectedForArchive(new Set());
    setSelectedForRestore(new Set());
    setSearchQuery("");
    setSortMode("default");
  }, [viewMode, selectedApplication]);

  // =====================================================
  //  RENDER
  // =====================================================

  const { displayUsers, activeCount, archivedCount } = getFilteredSortedUsers();

  return (
    <div style={{ padding: "1rem", maxWidth: 700, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h1 style={{ fontSize: "1.5rem", margin: 0 }}>Buyer Dashboard</h1>
        <button 
          onClick={handleLogout}
          style={{ 
            padding: "0.5rem 1rem", 
            backgroundColor: "#f44336", 
            color: "white", 
            border: "none",
            borderRadius: "4px",
            cursor: "pointer" 
          }}
        >
          Logout
        </button>
      </div>
      
      {/* Application Selector */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Select Application</h2>
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
          {Object.keys(APPLICATIONS).map(appId => (
            <button
              key={appId}
              onClick={() => setSelectedApplication(appId)}
              disabled={buyerData && !hasLicenseForApplication(appId)}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: selectedApplication === appId ? "#4CAF50" : "#e0e0e0",
                color: selectedApplication === appId ? "white" : (buyerData && !hasLicenseForApplication(appId)) ? "#999" : "black",
                border: "none",
                borderRadius: "4px",
                cursor: (buyerData && !hasLicenseForApplication(appId)) ? "default" : "pointer",
                fontWeight: selectedApplication === appId ? "bold" : "normal",
                opacity: (buyerData && !hasLicenseForApplication(appId)) ? 0.7 : 1
              }}
            >
              {APPLICATIONS[appId].shortName}
            </button>
          ))}
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div style={{ 
          color: "red", 
          marginBottom: "1rem", 
          padding: "0.5rem", 
          backgroundColor: "#ffebee",
          borderRadius: "4px" 
        }}>
          {error}
        </div>
      )}

      {/* Bulk Results Display */}
      {bulkResults && showBulkResults && (
        <div style={{ 
          border: "1px solid #4CAF50", 
          padding: "1rem", 
          marginBottom: "1rem",
          borderRadius: "4px",
          backgroundColor: "#f1f8e9"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
            <h3 style={{ fontSize: "1.1rem", margin: 0, color: "#2e7d32" }}>Bulk Assignment Results</h3>
            <button 
              onClick={() => setShowBulkResults(false)}
              style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "#666" }}
            >
              ×
            </button>
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", fontSize: "0.9rem" }}>
            <div><strong>Total Processed:</strong></div>
            <div>{bulkResults.totalProcessed}</div>
            <div><strong>Successfully Assigned:</strong></div>
            <div style={{ color: "green" }}>{bulkResults.successful}</div>
            <div><strong>Already Assigned:</strong></div>
            <div style={{ color: "orange" }}>{bulkResults.alreadyAssigned}</div>
            <div><strong>Failed:</strong></div>
            <div style={{ color: "red" }}>{bulkResults.failed}</div>
            <div><strong>Invalid Rows:</strong></div>
            <div style={{ color: "red" }}>{bulkResults.invalidRows}</div>
            <div><strong>Invitations Sent:</strong></div>
            <div style={{ color: "blue" }}>{bulkResults.invitationsSent}</div>
            {bulkResults.duplicatesRemoved > 0 && (
              <>
                <div><strong>Duplicates Removed:</strong></div>
                <div style={{ color: "gray" }}>{bulkResults.duplicatesRemoved}</div>
              </>
            )}
          </div>
          
          {bulkResults.details && (
            <details style={{ marginTop: "0.5rem" }}>
              <summary style={{ cursor: "pointer", color: "#1976d2" }}>View Details</summary>
              <div style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>
                {bulkResults.details.failed.length > 0 && (
                  <div style={{ marginBottom: "0.5rem" }}>
                    <strong style={{ color: "red" }}>Failed:</strong>
                    <ul style={{ margin: "0.25rem 0", paddingLeft: "1rem" }}>
                      {bulkResults.details.failed.map((fail, idx) => (
                        <li key={idx}>{fail.email}: {fail.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {bulkResults.details.alreadyAssigned.length > 0 && (
                  <div style={{ marginBottom: "0.5rem" }}>
                    <strong style={{ color: "orange" }}>Already Assigned:</strong>
                    <ul style={{ margin: "0.25rem 0", paddingLeft: "1rem" }}>
                      {bulkResults.details.alreadyAssigned.map((email, idx) => (
                        <li key={idx}>{email}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {bulkResults.details.invalidRows.length > 0 && (
                  <div>
                    <strong style={{ color: "red" }}>Invalid Rows:</strong>
                    <ul style={{ margin: "0.25rem 0", paddingLeft: "1rem" }}>
                      {bulkResults.details.invalidRows.map((invalid, idx) => (
                        <li key={idx}>Row {invalid.row}: {invalid.email} - {invalid.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
      )}
      
      {loading && !buyerData ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Loading license data...</p>
        </div>
      ) : buyerData ? (
        <div>
          {hasLicenseForApplication(selectedApplication) ? (
            <>
              {/* License Information */}
              <div style={{ 
                border: "1px solid #ccc", 
                padding: "1rem", 
                marginBottom: "1rem",
                borderRadius: "4px" 
              }}>
                <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>
                  {APPLICATIONS[selectedApplication]?.shortName || selectedApplication} License Information
                </h2>

                {/* Buyer self-assign */}
                <button
                  style={{padding:"0.5rem 1rem",marginBottom:"1rem",
                          background:"#4CAF50",color:"#fff",border:"none",borderRadius:"4px"}}
                  onClick={async()=>{
                     const token = localStorage.getItem("token");
                     await fetch("/buyer/self-assign",{
                       method:"POST",
                       headers:{ "Content-Type":"application/json",
                                 Authorization:`Bearer ${token}`},
                       body:JSON.stringify({ 
                          application:selectedApplication,
                          buyerEmail:  buyerData.BuyerEmail
                       })
                     });
                     await fetchBuyerDetails();
                     setSuccessMessage("Licence assigned to you.");
                }}>
                  Assign one licence to <u>me</u>
                </button>
                {selfAssignedApps.includes(selectedApplication) && (
                  <button
                    style={{
                      padding: "0.5rem 1rem",
                      marginBottom: "1rem",
                      marginLeft: "0.5rem",
                      background: "#2196F3",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px"
                    }}
                    onClick={() => navigate("/buyer/launch")}
                  >
                    Launch {APPLICATIONS[selectedApplication].shortName}
                  </button>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                  <div><strong>Buyer Email:</strong></div>
                  <div>{buyerData.BuyerEmail}</div>
                  <div><strong>Total Licenses:</strong></div>
                  <div>{buyerData.ApplicationLicenses?.[selectedApplication] || 0}</div>
                  <div><strong>Used Licenses:</strong></div>
                  <div>{getActivatedUsersCount(selectedApplication)}</div>
                  <div><strong>Available Licenses:</strong></div>
                  <div>
                    {(buyerData.ApplicationLicenses?.[selectedApplication] || 0) - getActivatedUsersCount(selectedApplication)}
                  </div>
                  <div><strong>Assigned Users:</strong></div>
                  <div>{activeCount}{archivedCount > 0 ? ` (${archivedCount} archived)` : ""}</div>
                </div>
              </div>
              
              {/* Assign New License */}
              <div style={{ 
                border: "1px solid #ccc", 
                padding: "1rem", 
                marginBottom: "1rem",
                borderRadius: "4px" 
              }}>
                <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>
                  Assign New {APPLICATIONS[selectedApplication]?.shortName || selectedApplication} License
                </h2>
                
                {/* Single User Assignment */}
                <div style={{ marginBottom: "1.5rem" }}>
                  <h3 style={{ fontSize: "1rem", margin: "0 0 0.5rem 0" }}>Single User Assignment</h3>
                  <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="Enter user email..."
                      style={{ 
                        flex: 1, 
                        padding: "0.5rem",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        minWidth: "200px"
                      }}
                    />
                    <div>
                      <button 
                        onClick={assignLicense} 
                        disabled={
                          loading || 
                          !newUserEmail || 
                          (getUsersWithSelectedApplication().length >= (buyerData.ApplicationLicenses?.[selectedApplication] || 0))
                        }
                        style={{ 
                          padding: "0.5rem 1rem",
                          backgroundColor: (getUsersWithSelectedApplication().length >= (buyerData.ApplicationLicenses?.[selectedApplication] || 0)) 
                            ? "#ccc" : "#ff9800",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: (
                            loading || !newUserEmail || 
                            (getUsersWithSelectedApplication().length >= (buyerData.ApplicationLicenses?.[selectedApplication] || 0))
                          ) ? "default" : "pointer"
                        }}
                      >
                        {loading ? "Assigning..." : "Assign License"}
                      </button>
                      
                      {getUsersWithSelectedApplication().length >= (buyerData.ApplicationLicenses?.[selectedApplication] || 0) && (
                        <div style={{ color: "red", fontSize: "0.8rem", marginTop: "0.25rem" }}>
                          No licenses available for {APPLICATIONS[selectedApplication]?.shortName || selectedApplication}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* CSV Bulk Assignment */}
                <div style={{ borderTop: "1px solid #eee", paddingTop: "1rem" }}>
                  <h3 style={{ fontSize: "1rem", margin: "0 0 0.5rem 0" }}>Bulk Assignment (CSV Upload)</h3>
                  <div style={{ marginBottom: "0.5rem" }}>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                      style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc" }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                    <button 
                      onClick={handleCsvUpload} 
                      disabled={
                        csvUploading || !csvFile || 
                        (getUsersWithSelectedApplication().length >= (buyerData.ApplicationLicenses?.[selectedApplication] || 0))
                      }
                      style={{ 
                        padding: "0.5rem 1rem",
                        backgroundColor: (!csvFile || (getUsersWithSelectedApplication().length >= (buyerData.ApplicationLicenses?.[selectedApplication] || 0))) 
                          ? "#ccc" : "#673ab7",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: (csvUploading || !csvFile || (getUsersWithSelectedApplication().length >= (buyerData.ApplicationLicenses?.[selectedApplication] || 0))) 
                          ? "default" : "pointer"
                      }}
                    >
                      {csvUploading ? "Uploading..." : "Upload CSV"}
                    </button>
                    <div style={{ fontSize: "0.8rem", color: "#666" }}>
                      CSV should contain an "email" column with user email addresses
                    </div>
                  </div>
                  
                  <details style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>
                    <summary style={{ cursor: "pointer", color: "#1976d2" }}>CSV Format Help</summary>
                    <div style={{ marginTop: "0.5rem", padding: "0.5rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                      <p><strong>Your CSV file should have:</strong></p>
                      <ul style={{ margin: "0.5rem 0", paddingLeft: "1rem" }}>
                        <li>A header row with column names</li>
                        <li>An "email" column (or "Email", "user email", "email address", etc.)</li>
                        <li>One email address per row</li>
                      </ul>
                      <p><strong>Example:</strong></p>
                      <pre style={{ backgroundColor: "#fff", padding: "0.5rem", border: "1px solid #ddd", borderRadius: "2px" }}>
{`email
user1@example.com
user2@example.com
user3@example.com`}
                      </pre>
                    </div>
                  </details>
                </div>
                
                <p style={{ fontSize: "0.9rem", color: "#666", marginTop: "0.5rem" }}>
                  Users will automatically receive an invitation email when assigned a license.
                </p>
              </div>
              
              {/* Success Message */}
              {successMessage && (
                <div style={{ 
                  border: "1px solid #4CAF50", 
                  padding: "1rem", 
                  marginBottom: "1rem",
                  borderRadius: "4px",
                  backgroundColor: "#f1f8e9"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <h2 style={{ fontSize: "1.2rem", margin: 0, color: "#2e7d32" }}>Action Result</h2>
                    <button 
                      onClick={() => setSuccessMessage("")}
                      style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "#666" }}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{ 
                    color: "#2e7d32", 
                    padding: "0.5rem", 
                    backgroundColor: "#e8f5e9",
                    borderRadius: "4px",
                    border: "1px solid #4CAF50"
                  }}>
                    {successMessage}
                  </div>
                </div>
              )}           

              {/* ========================================================= */}
              {/*  USERS LIST — with Tags, Archive, Search & Sort controls   */}
              {/* ========================================================= */}
              {getUsersWithSelectedApplication().length > 0 && (
                <div style={{ 
                  border: "1px solid #ccc", 
                  padding: "1rem",
                  borderRadius: "4px"
                }}>
                  {/* View toggle: Active / Archive */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
                    <h2 style={{ fontSize: "1.2rem", margin: 0 }}>
                      {viewMode === "active" 
                        ? `${APPLICATIONS[selectedApplication]?.shortName} Assigned Users` 
                        : `${APPLICATIONS[selectedApplication]?.shortName} Archived Users`}
                    </h2>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        onClick={() => setShowTagManager(!showTagManager)}
                        style={{
                          padding: "0.4rem 0.75rem",
                          backgroundColor: showTagManager ? "#e65100" : "#ef6c00",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.85rem"
                        }}
                      >
                        {showTagManager ? "Close Tags" : "Manage Tags"}
                      </button>
                      <button
                        onClick={() => setViewMode(viewMode === "active" ? "archive" : "active")}
                        style={{
                          padding: "0.4rem 0.75rem",
                          backgroundColor: viewMode === "archive" ? "#2196F3" : "#78909C",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.85rem"
                        }}
                      >
                        {viewMode === "active" 
                          ? `View Archive${archivedCount > 0 ? ` (${archivedCount})` : ""}` 
                          : `View Active Users (${activeCount})`}
                      </button>
                    </div>
                  </div>

                  {/* ─── Tag Manager Panel ─── */}
                  {showTagManager && (
                    <div style={{
                      padding: "0.75rem",
                      marginBottom: "0.75rem",
                      backgroundColor: "#fff3e0",
                      borderRadius: "4px",
                      border: "1px solid #ffe0b2"
                    }}>
                      <div style={{ fontWeight: "bold", marginBottom: "0.5rem", fontSize: "0.95rem" }}>Tag Manager</div>
                      
                      {/* Create new tag */}
                      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                        <input
                          type="text"
                          value={newTagName}
                          onChange={(e) => setNewTagName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") createTag(); }}
                          placeholder="New tag name..."
                          style={{ flex: 1, padding: "0.35rem 0.5rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                        />
                        <button
                          onClick={createTag}
                          disabled={!newTagName.trim() || tagActionLoading}
                          style={{
                            padding: "0.35rem 0.75rem",
                            backgroundColor: !newTagName.trim() ? "#ccc" : "#ef6c00",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: !newTagName.trim() ? "default" : "pointer",
                            fontSize: "0.85rem"
                          }}
                        >
                          Create
                        </button>
                      </div>

                      {/* Existing tags */}
                      {buyerTags.length === 0 ? (
                        <div style={{ fontSize: "0.85rem", color: "#666", fontStyle: "italic" }}>
                          No tags created yet. Create one above to start organizing users.
                        </div>
                      ) : (
                        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                          {buyerTags.map(tag => {
                            const tagUserCount = (buyerData?.Users || []).filter(u => u.tag === tag && userHasApplicationAccess(u, selectedApplication)).length;
                            return (
                              <li key={tag} style={{
                                display: "flex", alignItems: "center", gap: "0.5rem",
                                padding: "0.35rem 0.5rem", marginBottom: "0.25rem",
                                backgroundColor: "#fff", borderRadius: "4px", border: "1px solid #eee"
                              }}>
                                {renamingTag && renamingTag.oldName === tag ? (
                                  <>
                                    <input
                                      type="text"
                                      value={renamingTag.newName}
                                      onChange={(e) => setRenamingTag({ ...renamingTag, newName: e.target.value })}
                                      onKeyDown={(e) => { if (e.key === "Enter") renameTag(tag, renamingTag.newName); if (e.key === "Escape") setRenamingTag(null); }}
                                      autoFocus
                                      style={{ flex: 1, padding: "0.2rem 0.4rem", borderRadius: "3px", border: "1px solid #ef6c00", fontSize: "0.85rem" }}
                                    />
                                    <button onClick={() => renameTag(tag, renamingTag.newName)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.85rem", color: "#4CAF50" }}>✓</button>
                                    <button onClick={() => setRenamingTag(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.85rem", color: "#999" }}>✕</button>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ flex: 1, fontSize: "0.85rem" }}>
                                      <strong>{tag}</strong>
                                      <span style={{ color: "#999", marginLeft: "0.5rem" }}>({tagUserCount} user{tagUserCount !== 1 ? "s" : ""})</span>
                                    </span>
                                    <button onClick={() => setRenamingTag({ oldName: tag, newName: tag })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.8rem", color: "#1976d2" }}>Rename</button>
                                    <button onClick={() => deleteTag(tag)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.8rem", color: "#f44336" }}>Delete</button>
                                  </>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Search, Tag Filter & Sort Controls */}
                  <div style={{ 
                    display: "flex", 
                    gap: "0.5rem", 
                    marginBottom: "0.75rem", 
                    flexWrap: "wrap",
                    alignItems: "center" 
                  }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by email..."
                      style={{ 
                        flex: 1, 
                        padding: "0.4rem 0.5rem", 
                        borderRadius: "4px", 
                        border: "1px solid #ccc",
                        minWidth: "120px",
                        fontSize: "0.85rem"
                      }}
                    />
                    {/* Tag Filter */}
                    <select
                      value={tagFilter}
                      onChange={(e) => setTagFilter(e.target.value)}
                      style={{ 
                        padding: "0.4rem 0.5rem", 
                        borderRadius: "4px", 
                        border: "1px solid #ccc",
                        fontSize: "0.85rem",
                        backgroundColor: tagFilter !== "__ALL__" ? "#fff3e0" : "#fff"
                      }}
                    >
                      <option value="__ALL__">All Tags</option>
                      <option value="__UNTAGGED__">Untagged</option>
                      {buyerTags.map(tag => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                    {/* Sort */}
                    <select
                      value={sortMode}
                      onChange={(e) => setSortMode(e.target.value)}
                      style={{ 
                        padding: "0.4rem 0.5rem", 
                        borderRadius: "4px", 
                        border: "1px solid #ccc",
                        fontSize: "0.85rem",
                        backgroundColor: "#fff"
                      }}
                    >
                      <option value="default">Default Order</option>
                      <option value="alpha-asc">Alphabetical A → Z</option>
                      <option value="alpha-desc">Alphabetical Z → A</option>
                      <option value="date-asc">Date (Oldest First)</option>
                      <option value="date-desc">Date (Newest First)</option>
                      <option value="progress-desc">Completion % (High → Low)</option>
                      <option value="progress-asc">Completion % (Low → High)</option>
                    </select>
                  </div>

                  {/* Select All + Action Buttons */}
                  {displayUsers.length > 0 && (
                    <div style={{ 
                      display: "flex", 
                      justifyContent: "space-between", 
                      alignItems: "center", 
                      marginBottom: "0.5rem",
                      padding: "0.5rem",
                      backgroundColor: viewMode === "active" ? "#fff3e0" : "#e3f2fd",
                      borderRadius: "4px",
                      border: `1px solid ${viewMode === "active" ? "#ffe0b2" : "#bbdefb"}`,
                      flexWrap: "wrap",
                      gap: "0.5rem"
                    }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9rem" }}>
                        <input
                          type="checkbox"
                          checked={
                            viewMode === "active"
                              ? displayUsers.length > 0 && displayUsers.every(u => selectedForArchive.has(u.userEmail))
                              : displayUsers.length > 0 && displayUsers.every(u => selectedForRestore.has(u.userEmail))
                          }
                          onChange={() => {
                            if (viewMode === "active") toggleSelectAllArchive(displayUsers);
                            else toggleSelectAllRestore(displayUsers);
                          }}
                          style={{ width: "16px", height: "16px" }}
                        />
                        <span style={{ fontWeight: "bold" }}>
                          Select All ({displayUsers.length})
                        </span>
                      </label>

                      {viewMode === "active" ? (
                        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                          {/* Tag Assign */}
                          {buyerTags.length > 0 && (
                            <button
                              onClick={() => setShowTagAssign(!showTagAssign)}
                              disabled={selectedForArchive.size === 0}
                              style={{
                                padding: "0.35rem 0.75rem",
                                backgroundColor: selectedForArchive.size === 0 ? "#ccc" : "#ef6c00",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: selectedForArchive.size === 0 ? "default" : "pointer",
                                fontSize: "0.85rem",
                                fontWeight: "bold"
                              }}
                            >
                              Tag ({selectedForArchive.size})
                            </button>
                          )}
                          {/* Resend Invitation */}
                          <button
                            onClick={bulkResendInvitations}
                            disabled={selectedForArchive.size === 0 || bulkActionLoading}
                            style={{
                              padding: "0.35rem 0.75rem",
                              backgroundColor: selectedForArchive.size === 0 ? "#ccc" : "#2196F3",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: selectedForArchive.size === 0 ? "default" : "pointer",
                              fontSize: "0.85rem",
                              fontWeight: "bold"
                            }}
                          >
                            {bulkActionLoading ? "..." : `Resend (${selectedForArchive.size})`}
                          </button>
                          {/* Revoke */}
                          <button
                            onClick={bulkRevokeSelected}
                            disabled={selectedForArchive.size === 0 || bulkActionLoading}
                            style={{
                              padding: "0.35rem 0.75rem",
                              backgroundColor: selectedForArchive.size === 0 ? "#ccc" : "#f44336",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: selectedForArchive.size === 0 ? "default" : "pointer",
                              fontSize: "0.85rem",
                              fontWeight: "bold"
                            }}
                          >
                            {bulkActionLoading ? "..." : `Revoke (${selectedForArchive.size})`}
                          </button>
                          {/* Archive */}
                          <button
                            onClick={archiveSelectedUsers}
                            disabled={selectedForArchive.size === 0 || archiving}
                            style={{
                              padding: "0.35rem 0.75rem",
                              backgroundColor: selectedForArchive.size === 0 ? "#ccc" : "#78909C",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: selectedForArchive.size === 0 ? "default" : "pointer",
                              fontSize: "0.85rem",
                              fontWeight: "bold"
                            }}
                          >
                            {archiving ? "..." : `Archive (${selectedForArchive.size})`}
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                          {/* Tag Assign — also in archive view */}
                          {buyerTags.length > 0 && (
                            <button
                              onClick={() => setShowTagAssign(!showTagAssign)}
                              disabled={selectedForRestore.size === 0}
                              style={{
                                padding: "0.35rem 0.75rem",
                                backgroundColor: selectedForRestore.size === 0 ? "#ccc" : "#ef6c00",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: selectedForRestore.size === 0 ? "default" : "pointer",
                                fontSize: "0.85rem",
                                fontWeight: "bold"
                              }}
                            >
                              Tag ({selectedForRestore.size})
                            </button>
                          )}
                          {/* Restore */}
                          <button
                            onClick={restoreSelectedUsers}
                            disabled={selectedForRestore.size === 0 || restoring}
                            style={{
                              padding: "0.35rem 0.75rem",
                              backgroundColor: selectedForRestore.size === 0 ? "#ccc" : "#2196F3",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: selectedForRestore.size === 0 ? "default" : "pointer",
                              fontSize: "0.85rem",
                              fontWeight: "bold"
                            }}
                          >
                            {restoring ? "Restoring..." : `Restore (${selectedForRestore.size})`}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tag Assignment Inline Panel */}
                  {showTagAssign && (viewMode === "active" ? selectedForArchive.size > 0 : selectedForRestore.size > 0) && (
                    <div style={{
                      display: "flex", gap: "0.5rem", alignItems: "center",
                      padding: "0.5rem",
                      marginBottom: "0.5rem",
                      backgroundColor: "#fff8e1",
                      borderRadius: "4px",
                      border: "1px solid #ffe082",
                      flexWrap: "wrap"
                    }}>
                      <span style={{ fontSize: "0.85rem" }}>Assign tag to {viewMode === "active" ? selectedForArchive.size : selectedForRestore.size} user(s):</span>
                      <select
                        value={assignTagValue}
                        onChange={(e) => setAssignTagValue(e.target.value)}
                        style={{ padding: "0.3rem 0.5rem", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.85rem" }}
                      >
                        <option value="">-- Select tag --</option>
                        {buyerTags.map(tag => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                        <option value="__REMOVE__">Remove tag</option>
                      </select>
                      <button
                        onClick={assignTagToSelected}
                        disabled={!assignTagValue || tagActionLoading}
                        style={{
                          padding: "0.3rem 0.6rem",
                          backgroundColor: !assignTagValue ? "#ccc" : "#ef6c00",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: !assignTagValue ? "default" : "pointer",
                          fontSize: "0.85rem"
                        }}
                      >
                        {tagActionLoading ? "..." : "Apply"}
                      </button>
                      <button
                        onClick={() => { setShowTagAssign(false); setAssignTagValue(""); }}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: "1rem" }}
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* Users list */}
                  {displayUsers.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "1rem", color: "#666", fontStyle: "italic" }}>
                      {searchQuery 
                        ? `No ${viewMode === "active" ? "" : "archived "}users match "${searchQuery}"`
                        : tagFilter !== "__ALL__"
                          ? `No ${viewMode === "active" ? "" : "archived "}users with ${tagFilter === "__UNTAGGED__" ? "no tag" : `tag "${tagFilter}"`}`
                          : viewMode === "archive" 
                            ? "No archived users" 
                            : "No active users"}
                    </div>
                  ) : (
                    <ul style={{ listStyleType: "none", padding: 0 }}>
                      {displayUsers.map((user) => {
                        const progress = getUserProgressData(user.userEmail, selectedApplication);
                        const isActivated = userHasActivatedApplication(user, selectedApplication);
                        const isSelected = viewMode === "active" 
                          ? selectedForArchive.has(user.userEmail) 
                          : selectedForRestore.has(user.userEmail);
                        
                        return (
                          <li 
                            key={user.userEmail} 
                            style={{ 
                              padding: "0.75rem",
                              marginBottom: "0.5rem",
                              backgroundColor: isSelected ? (viewMode === "active" ? "#fff8e1" : "#e3f2fd") : "#f5f5f5",
                              borderRadius: "4px",
                              border: isSelected ? `2px solid ${viewMode === "active" ? "#ff9800" : "#2196F3"}` : "2px solid transparent"
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                              <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                                {/* Archive / Restore checkbox */}
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {
                                    if (viewMode === "active") toggleArchiveSelection(user.userEmail);
                                    else toggleRestoreSelection(user.userEmail);
                                  }}
                                  style={{ width: "16px", height: "16px", marginTop: "2px", cursor: "pointer" }}
                                />
                                <div>
                                  <div style={{ fontWeight: "bold" }}>
                                    {user.userEmail}
                                    {user.tag && (
                                      <span style={{
                                        marginLeft: "0.5rem",
                                        padding: "0.1rem 0.4rem",
                                        backgroundColor: "#ef6c00",
                                        color: "white",
                                        borderRadius: "10px",
                                        fontSize: "0.7rem",
                                        fontWeight: "normal",
                                        verticalAlign: "middle"
                                      }}>
                                        {user.tag}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: "0.8rem", color: isActivated ? "green" : "orange" }}>
                                    {APPLICATIONS[selectedApplication]?.shortName || selectedApplication} Status: {
                                      isActivated ? "Activated" : "Not Activated"
                                    }
                                    {user.cognitoStatus && ` - ${user.cognitoStatus}`}
                                  </div>
                                  {viewMode === "archive" && user.archivedAt && (
                                    <div style={{ fontSize: "0.75rem", color: "#78909C" }}>
                                      Archived: {new Date(user.archivedAt).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Action buttons — only in active view */}
                              {viewMode === "active" && (
                                <div>
                                  {!isActivated && (
                                    <button 
                                      onClick={() => {
                                        setButtonStates(prev => ({
                                          ...prev,
                                          [`${user.userEmail}-revoke`]: { text: 'REVOKED', color: '#FF9800' }
                                        }));
                                        setTimeout(() => {
                                          setButtonStates(prev => ({
                                            ...prev,
                                            [`${user.userEmail}-revoke`]: null
                                          }));
                                        }, 2000);
                                        revokeLicense(user.userEmail);
                                      }}
                                      style={{ 
                                        padding: "0.25rem 0.5rem",
                                        backgroundColor: buttonStates[`${user.userEmail}-revoke`]?.color || "#f44336",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        fontSize: "0.8rem"
                                      }}
                                    >
                                      {buttonStates[`${user.userEmail}-revoke`]?.text || "Revoke"}
                                    </button>
                                  )}
                                  {!isActivated && !user.cognitoStatus && user.userEmail !== buyerData.BuyerEmail && (
                                    <button 
                                      onClick={() => {
                                        setButtonStates(prev => ({
                                          ...prev,
                                          [`${user.userEmail}-invite`]: { text: 'SENT', color: '#FF9800' }
                                        }));
                                        setTimeout(() => {
                                          setButtonStates(prev => ({
                                            ...prev,
                                            [`${user.userEmail}-invite`]: null
                                          }));
                                        }, 2000);
                                        inviteUser(user.userEmail);
                                      }}
                                      style={{ 
                                        padding: "0.25rem 0.5rem",
                                        backgroundColor: buttonStates[`${user.userEmail}-invite`]?.color || "#2196F3",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        fontSize: "0.8rem",
                                        marginLeft: "0.5rem"
                                      }}
                                    >
                                      {buttonStates[`${user.userEmail}-invite`]?.text || "Resend Invitation"}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Progress Information */}
                            {progress.loaded ? (
                              progress.exists && progress.data ? (
                                <div style={{ 
                                  backgroundColor: "#fff", 
                                  padding: "0.5rem", 
                                  borderRadius: "4px",
                                  fontSize: "0.9rem",
                                  border: "1px solid #eee"
                                }}>
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.25rem" }}>
                                    <div><strong>Play Time:</strong></div>
                                    <div>{formatPlayTime(progress.data.playTime)}</div>
                                    <div><strong>Checkpoint:</strong></div>
                                    <div>
                                      {progress.data.checkPoint} ({calculateCheckpointPercentage(progress.data.checkPoint, selectedApplication)})
                                    </div>
                                    <div><strong>AAR Checkpoint:</strong></div>
                                    <div>{progress.data.aarCheckpoint}</div>
                                    <div><strong>Completed:</strong></div>
                                    <div style={{ color: progress.data.gameComplete ? "green" : "inherit" }}>
                                      {progress.data.gameComplete ? "Yes" : "No"}
                                    </div>
                                    {progress.data.gameComplete && progress.data.dateCompleted && progress.data.dateCompleted !== "0001-01-01T00:00:00" && (
                                      <>
                                        <div><strong>Completion Date:</strong></div>
                                        <div>{formatDate(progress.data.dateCompleted)}</div>
                                      </>
                                    )}
                                    <div><strong>Last Played:</strong></div>
                                    <div>{formatDate(progress.data.lastUpdated)}</div>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ fontSize: "0.85rem", color: "#666", fontStyle: "italic" }}>
                                  No gameplay data available yet for {APPLICATIONS[selectedApplication]?.shortName || selectedApplication}
                                </div>
                              )
                            ) : (
                              <div style={{ fontSize: "0.85rem", color: "#666", fontStyle: "italic" }}>
                                Loading progress data...
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: "2rem", textAlign: "center", border: "1px solid #ccc", borderRadius: "4px" }}>
              <p>You don't have any licenses for {APPLICATIONS[selectedApplication]?.shortName || selectedApplication}.</p>
              <p>Please select another application or contact support to purchase licenses.</p>
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: "2rem", textAlign: "center", border: "1px solid #ccc", borderRadius: "4px" }}>
          <p>No license data available. Please contact support.</p>
        </div>
      )}
     <div style={{ marginTop: "2rem", textAlign: "center", fontSize: "0.9rem" }}>
          Need help?{" "}
          <a 
            href="mailto:support@creativetechnologiesincla.zohodesk.com" 
            style={{ color: "#2196F3", textDecoration: "none" }}
          >
            Contact Support
          </a>
        </div>
    </div>
  );
}

export default BuyerDashboard;
